import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, FileUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadFile } from "@/components/SignPanel";
import { errMsg, fmtDateTime, openPdf, api } from "@/lib/api";

export function FilesPanel({ cid, categories }) {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState(categories[0]);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/contracts/${cid}/files`).then((r) => setFiles(r.data));
  }, [cid]);
  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await uploadFile(cid, file, category, note);
      toast.success("Allegato caricato");
      setFile(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try { await api.delete(`/files/${id}`); toast.success("Allegato rimosso"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cls = "rounded-md border border-[#D4E6EC] bg-white px-3 py-2 text-sm";

  return (
    <div className="space-y-6 fade-up">
      <section className="rounded-xl border border-[#D4E6EC] bg-white p-5 sm:p-7 space-y-4" data-testid="files-upload-form">
        <h3 className="font-serif-it text-2xl font-semibold">Carica allegato</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <select className={cls} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="file-category-select">
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input className={cls} placeholder="Nota (facoltativa)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="file-note-input" />
          <label className={`${cls} cursor-pointer flex items-center gap-2 truncate`}>
            <FileUp size={15} /> <span className="truncate">{file ? file.name : "Scegli file (PDF, JPG, PNG)"}</span>
            <input type="file" className="hidden" accept="application/pdf,image/*" data-testid="file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <Button className="rounded-full" disabled={!file || busy} onClick={submit} data-testid="file-upload-button">{busy ? "Caricamento…" : "Carica"}</Button>
      </section>
      <div className="rounded-xl border border-[#D4E6EC] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="files-table">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#7B929C] border-b border-[#D4E6EC]">
            <tr><th className="p-3">Categoria</th><th className="p-3">File</th><th className="p-3">Caricato</th><th className="p-3">Impronta SHA-256</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {files.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#7B929C]" data-testid="files-empty">Nessun allegato caricato.</td></tr>}
            {files.map((f) => (
              <tr key={f.id} className="border-b border-[#EAF5F7]" data-testid={`file-row-${f.id}`}>
                <td className="p-3 font-medium">{f.category}{f.note && <div className="text-xs text-[#7B929C] font-normal">{f.note}</div>}</td>
                <td className="p-3">{f.original_filename}<div className="text-xs text-[#7B929C]">{(f.size / 1024).toFixed(0)} KB</div></td>
                <td className="p-3 text-xs">{fmtDateTime(f.created_at)}<div className="text-[#7B929C]">{f.uploaded_by}</div></td>
                <td className="p-3 font-mono-it text-[11px] text-[#47606B]">{f.sha256.slice(0, 16)}…</td>
                <td className="p-3 whitespace-nowrap text-right">
                  <button className="p-2 hover:bg-[#EAF5F7] rounded-md" title="Apri" onClick={() => openPdf(`/files/${f.id}/download`)} data-testid={`file-open-${f.id}`}><Eye size={15} /></button>
                  <button className="p-2 hover:bg-[#FEE2E2] rounded-md text-[#991B1B]" title="Rimuovi" onClick={() => remove(f.id)} data-testid={`file-delete-${f.id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AuditPanel({ cid, version }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/contracts/${cid}/audit`).then((r) => setRows(r.data));
  }, [cid, version]);
  return (
    <div className="rounded-xl border border-[#D4E6EC] bg-white overflow-x-auto fade-up">
      <table className="w-full text-sm" data-testid="audit-table">
        <thead className="text-left text-[11px] uppercase tracking-wider text-[#7B929C] border-b border-[#D4E6EC]">
          <tr><th className="p-3">Data e ora</th><th className="p-3">Azione</th><th className="p-3">Dettagli</th><th className="p-3">Utente</th><th className="p-3">IP / dispositivo</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-[#EAF5F7] align-top" data-testid="audit-row">
              <td className="p-3 whitespace-nowrap text-xs">{fmtDateTime(r.at)}</td>
              <td className="p-3 font-medium">{r.action}</td>
              <td className="p-3 text-xs text-[#47606B] break-all max-w-md">{r.details}</td>
              <td className="p-3 text-xs">{r.actor}</td>
              <td className="p-3 text-[11px] text-[#7B929C] max-w-[220px]">{r.ip}<div className="truncate" title={r.user_agent}>{r.user_agent}</div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
